import React, { useState, useEffect, useCallback } from "react";
import { Station, StationWithAvailability } from "../types";
import "./LocationStationSearch.css";
import StationSearch from "./StationSearch";

const GBFS_BASE_URL = "https://gbfs.lyft.com/gbfs/2.3/bkn/en";

interface LocationStationSearchProps {
  onStationSelect: (station: Station) => void;
}

interface UserLocation {
  lat: number;
  lon: number;
}

const LocationStationSearch: React.FC<LocationStationSearchProps> = ({
  onStationSelect
}) => {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [nearbyStations, setNearbyStations] = useState<
    StationWithAvailability[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationPermission, setLocationPermission] = useState<
    "granted" | "denied" | "prompt" | "loading"
  >("prompt");
  const [searchMode, setSearchMode] = useState<"location" | "manual">(
    "location"
  );

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = useCallback(
    (lat1: number, lon1: number, lat2: number, lon2: number): number => {
      const R = 3959; // Earth's radius in miles
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLon = ((lon2 - lon1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    []
  );

  // Request user's location
  const requestLocation = useCallback(() => {
    setLocationPermission("loading");
    setError(null);

    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      setLocationPermission("denied");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const location = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };
        setUserLocation(location);
        setLocationPermission("granted");
        fetchNearbyStations(location);
      },
      error => {
        console.error("Geolocation error:", error);
        setLocationPermission("denied");
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setError(
              "Location access denied. Please enable location permissions and try again."
            );
            break;
          case error.POSITION_UNAVAILABLE:
            setError("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            setError("Location request timed out.");
            break;
          default:
            setError("An unknown error occurred while retrieving location.");
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }, []);

  // Fetch station information from GBFS API
  const fetchStationInfo = useCallback(async (): Promise<Station[]> => {
    try {
      const response = await fetch(`${GBFS_BASE_URL}/station_information.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data.stations;
    } catch (err) {
      console.error("Failed to fetch station information:", err);
      throw new Error("Failed to fetch station information");
    }
  }, []);

  // Fetch station status (bike availability) from GBFS API
  const fetchStationStatus = useCallback(async (): Promise<any[]> => {
    try {
      const response = await fetch(`${GBFS_BASE_URL}/station_status.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data.stations;
    } catch (err) {
      console.error("Failed to fetch station status:", err);
      throw new Error("Failed to fetch station status");
    }
  }, []);

  // Fetch nearby stations with availability data
  const fetchNearbyStations = useCallback(
    async (location: UserLocation) => {
      setLoading(true);
      setError(null);

      try {
        // Fetch both station info and status in parallel
        const [stationInfo, stationStatus] = await Promise.all([
          fetchStationInfo(),
          fetchStationStatus()
        ]);

        // Create a map of station status for quick lookup
        const statusMap = new Map();
        stationStatus.forEach((status: any) => {
          statusMap.set(status.station_id, status);
        });

        // Calculate distances and combine data
        const stationsWithDistance: StationWithAvailability[] = stationInfo
          .map((station: Station) => {
            const distance = calculateDistance(
              location.lat,
              location.lon,
              station.lat,
              station.lon
            );
            const status = statusMap.get(station.station_id);

            return {
              ...station,
              distance,
              num_bikes_available: status?.num_bikes_available || 0,
              num_ebikes_available: status?.num_ebikes_available || 0,
              num_docks_available: status?.num_docks_available || 0,
              is_renting: status?.is_renting ?? true,
              is_returning: status?.is_returning ?? true,
              is_installed: status?.is_installed ?? true
            };
          })
          .filter((station: StationWithAvailability) => station.is_installed) // Only show installed stations
          .sort(
            (a: StationWithAvailability, b: StationWithAvailability) =>
              a.distance! - b.distance!
          )
          .slice(0, 20); // Get top 20 nearest stations

        setNearbyStations(stationsWithDistance);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch station data"
        );
        setNearbyStations([]);
      } finally {
        setLoading(false);
      }
    },
    [calculateDistance, fetchStationInfo, fetchStationStatus]
  );

  const handleStationClick = (station: StationWithAvailability) => {
    onStationSelect(station);
  };

  const formatDistance = (distance: number): string => {
    if (distance < 0.1) {
      return `${Math.round(distance * 5280)} ft`;
    }
    return `${distance.toFixed(1)} mi`;
  };

  const getAvailabilityColor = (
    bikesAvailable: number,
    totalCapacity: number
  ): string => {
    const percentage =
      totalCapacity > 0 ? (bikesAvailable / totalCapacity) * 100 : 0;
    if (percentage >= 50) return "#4CAF50"; // Green
    if (percentage >= 25) return "#FF9800"; // Orange
    return "#F44336"; // Red
  };

  if (locationPermission === "prompt") {
    return (
      <div className="location-station-search">
        <div className="search-mode-selector">
          <button
            className={`mode-button ${
              searchMode === "location" ? "active" : ""
            }`}
            onClick={() => setSearchMode("location")}
          >
            📍 Nearby Stations
          </button>
          <button
            className={`mode-button ${searchMode === "manual" ? "active" : ""}`}
            onClick={() => setSearchMode("manual")}
          >
            🔍 Manual Search
          </button>
        </div>

        {searchMode === "location" ? (
          <div className="location-prompt">
            <h3>📍 Find Nearby CitiBike Stations</h3>
            <p>
              Allow location access to see the 20 nearest CitiBike stations with
              real-time availability.
            </p>
            <button onClick={requestLocation} className="location-button">
              📍 Use My Location
            </button>
          </div>
        ) : (
          <div className="manual-search">
            <h3>🔍 Search CitiBike Stations</h3>
            <p>Search for any CitiBike station by name</p>
            <StationSearch onStationSelect={onStationSelect} />
          </div>
        )}
      </div>
    );
  }

  if (locationPermission === "loading") {
    return (
      <div className="location-station-search">
        <div className="loading">Getting your location...</div>
      </div>
    );
  }

  if (locationPermission === "denied" || error) {
    return (
      <div className="location-station-search">
        <div className="error">
          <h3>❌ Location Access Required</h3>
          <p>{error}</p>
          <button onClick={requestLocation} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="location-station-search">
      <div className="search-mode-selector">
        <button
          className={`mode-button ${searchMode === "location" ? "active" : ""}`}
          onClick={() => setSearchMode("location")}
        >
          📍 Nearby Stations
        </button>
        <button
          className={`mode-button ${searchMode === "manual" ? "active" : ""}`}
          onClick={() => setSearchMode("manual")}
        >
          🔍 Manual Search
        </button>
      </div>

      {searchMode === "location" ? (
        <>
          <div className="search-header">
            <h3>🚴‍♂️ Nearby CitiBike Stations</h3>
            <p>Showing the 20 nearest stations to your location</p>
            <button
              onClick={() => fetchNearbyStations(userLocation!)}
              className="refresh-button"
            >
              🔄 Refresh
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading nearby stations...</div>
          ) : (
            <div className="stations-list">
              {nearbyStations.map(station => (
                <div
                  key={station.station_id}
                  className="station-item"
                  onClick={() => handleStationClick(station)}
                >
                  <div className="station-header">
                    <div className="station-name">{station.name}</div>
                    <div className="station-distance">
                      {formatDistance(station.distance!)}
                    </div>
                  </div>

                  <div className="station-availability">
                    <div className="availability-item">
                      <span className="availability-label">🚴‍♂️ Bikes:</span>
                      <span
                        className="availability-value"
                        style={{
                          color: getAvailabilityColor(
                            station.num_bikes_available || 0,
                            station.capacity
                          )
                        }}
                      >
                        {station.num_bikes_available || 0}
                      </span>
                    </div>

                    <div className="availability-item">
                      <span className="availability-label">⚡ E-bikes:</span>
                      <span
                        className="availability-value"
                        style={{
                          color: getAvailabilityColor(
                            station.num_ebikes_available || 0,
                            station.capacity
                          )
                        }}
                      >
                        {station.num_ebikes_available || 0}
                      </span>
                    </div>

                    <div className="availability-item">
                      <span className="availability-label">
                        🔗 Docks Available:
                      </span>
                      <span className="availability-value">
                        {station.num_docks_available || 0}
                      </span>
                    </div>
                  </div>

                  <div className="station-status">
                    {!station.is_renting && (
                      <span className="status-badge unavailable">
                        No Renting
                      </span>
                    )}
                    {!station.is_returning && (
                      <span className="status-badge unavailable">
                        No Returns
                      </span>
                    )}
                    {station.is_renting && station.is_returning && (
                      <span className="status-badge available">Active</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {nearbyStations.length === 0 && !loading && (
            <div className="no-stations">
              <p>No stations found near your location.</p>
            </div>
          )}
        </>
      ) : (
        <div className="manual-search">
          <h3>🔍 Search CitiBike Stations</h3>
          <p>Search for any CitiBike station by name</p>
          <StationSearch onStationSelect={onStationSelect} />
        </div>
      )}
    </div>
  );
};

export default LocationStationSearch;
